import React, { useState } from 'react'
import styled from 'styled-components'

interface PropertyImageCarouselProps {
  images: string[]
  alt: string
}

const CarouselContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
`

const CarouselImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`

const NavigationButton = styled.button<{ direction: 'prev' | 'next' }>`
  position: absolute;
  top: 50%;
  ${({ direction }) => (direction === 'prev' ? 'left: 8px;' : 'right: 8px;')}
  transform: translateY(-50%);
  background: rgba(22, 41, 88, 0.2);
  color: white;
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;
  z-index: 2;

  &:hover {
    background: rgba(22, 41, 88, 0.4);
  }

  &:disabled {
    display: none;
  }

  svg {
    width: 1em;
    height: 1em;
    fill: currentColor;
  }
`

const ArrowIcon = styled.svg<{ direction: 'prev' | 'next' }>`
  width: 1em;
  height: 1em;
  fill: currentColor;
  transform: ${({ direction }) =>
    direction === 'prev' ? 'rotate(180deg)' : 'rotate(0deg)'};
`

const ImageCounter = styled.div`
  position: absolute;
  bottom: 8px;
  right: 8px;
  background: rgba(22, 41, 88, 0.2);
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  z-index: 2;
`

export const PropertyImageCarousel: React.FC<PropertyImageCarouselProps> = ({
  images,
  alt,
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0)

  if (!images || images.length === 0) {
    return null
  }

  const hasMultipleImages = images.length > 1

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length)
  }

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length)
  }

  return (
    <CarouselContainer>
      <CarouselImage
        src={images[currentImageIndex]}
        alt={`${alt} - Image ${currentImageIndex + 1}`}
      />

      {hasMultipleImages && (
        <>
          <NavigationButton direction="prev" onClick={prevImage}>
            <ArrowIcon
              direction="prev"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                // eslint-disable-next-line max-len
                d="M13.3543 7.56801H0.646625C0.328932 7.56801 0.0722656 7.31135 0.0722656 6.99366C0.0722656 6.67596 0.328932 6.4193 0.646625 6.4193H11.9669L8.48124 2.93367C8.25688 2.70932 8.25688 2.34496 8.48124 2.1206C8.7056 1.89624 9.06996 1.89624 9.29432 2.1206L13.7618 6.58802C13.9269 6.75314 13.9753 6.99904 13.8856 7.21442C13.7959 7.42801 13.5859 7.56801 13.3543 7.56801Z"
                fill="currentColor"
              />
              <path
                // eslint-disable-next-line max-len
                d="M8.88153 12.0433C8.73435 12.0433 8.58717 11.9877 8.47589 11.8746C8.25153 11.6503 8.25153 11.2859 8.47589 11.0616L12.9487 6.58874C13.1731 6.36438 13.5375 6.36438 13.7618 6.58874C13.9862 6.8131 13.9862 7.17746 13.7618 7.40182L9.28897 11.8746C9.17589 11.9877 9.02871 12.0433 8.88153 12.0433Z"
                fill="currentColor"
              />
            </ArrowIcon>
          </NavigationButton>
          <NavigationButton direction="next" onClick={nextImage}>
            <ArrowIcon
              direction="next"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                // eslint-disable-next-line max-len
                d="M13.3543 7.56801H0.646625C0.328932 7.56801 0.0722656 7.31135 0.0722656 6.99366C0.0722656 6.67596 0.328932 6.4193 0.646625 6.4193H11.9669L8.48124 2.93367C8.25688 2.70932 8.25688 2.34496 8.48124 2.1206C8.7056 1.89624 9.06996 1.89624 9.29432 2.1206L13.7618 6.58802C13.9269 6.75314 13.9753 6.99904 13.8856 7.21442C13.7959 7.42801 13.5859 7.56801 13.3543 7.56801Z"
                fill="currentColor"
              />
              <path
                // eslint-disable-next-line max-len
                d="M8.88153 12.0433C8.73435 12.0433 8.58717 11.9877 8.47589 11.8746C8.25153 11.6503 8.25153 11.2859 8.47589 11.0616L12.9487 6.58874C13.1731 6.36438 13.5375 6.36438 13.7618 6.58874C13.9862 6.8131 13.9862 7.17746 13.7618 7.40182L9.28897 11.8746C9.17589 11.9877 9.02871 12.0433 8.88153 12.0433Z"
                fill="currentColor"
              />
            </ArrowIcon>
          </NavigationButton>

          <ImageCounter>
            {currentImageIndex + 1} / {images.length}
          </ImageCounter>
        </>
      )}
    </CarouselContainer>
  )
}
